import React, { useState, useEffect, useCallback } from 'react';
import { X, Edit2, Trash2, Upload, Download, FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { Objective, CreateObjectiveRequest } from '../../types';
import { objectivesAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface ObjectiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicationId: number;
  onObjectiveUpdated: () => void;
}

const ObjectiveModal: React.FC<ObjectiveModalProps> = ({
  isOpen,
  onClose,
  publicationId,
  onObjectiveUpdated
}) => {
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingObjective, setEditingObjective] = useState<Objective | null>(null);
  const [formData, setFormData] = useState<CreateObjectiveRequest>({
    title: '',
    description: ''
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState<number | null>(null);

  const loadObjectives = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await objectivesAPI.getObjectives(publicationId);
      setObjectives(data);
    } catch (error: any) {
      console.error('Error loading objectives:', error);
      toast.error('Failed to load objectives');
    } finally {
      setIsLoading(false);
    }
  }, [publicationId]);

  useEffect(() => {
    if (isOpen) {
      loadObjectives();
    }
  }, [isOpen, loadObjectives]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (editingObjective) {
        await objectivesAPI.updateObjective(editingObjective.id, formData);
        toast.success('Objective updated successfully');
      } else {
        await objectivesAPI.createObjective(publicationId, formData);
        toast.success('Objective created successfully');
      }
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publicationId}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      setFormData({ title: '', description: '' });
      setEditingObjective(null);
      loadObjectives();
      onObjectiveUpdated();
    } catch (error: any) {
      console.error('Error saving objective:', error);
      toast.error(error.response?.data?.error || 'Failed to save objective');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (objective: Objective) => {
    setEditingObjective(objective);
    setFormData({
      title: objective.title,
      description: objective.description || ''
    });
  };

  const handleCancel = () => {
    setEditingObjective(null);
    setFormData({ title: '', description: '' });
  };

  const handleDelete = async (objectiveId: number) => {
    if (!window.confirm('Are you sure you want to delete this objective?')) {
      return;
    }

    try {
      await objectivesAPI.deleteObjective(objectiveId);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern(`/publications/${publicationId}/batch`);
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Objective deleted successfully');
      loadObjectives();
      onObjectiveUpdated();
    } catch (error: any) {
      console.error('Error deleting objective:', error);
      toast.error('Failed to delete objective');
    }
  };

  const handleFileUpload = async (objectiveId: number) => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploadingFile(objectiveId);
    
    try {
      await objectivesAPI.uploadAttachment(objectiveId, selectedFile);
      toast.success('File uploaded successfully');
      setSelectedFile(null);
      loadObjectives();
      onObjectiveUpdated();
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleFileDownload = async (objectiveId: number, attachmentId: number, filename: string) => {
    try {
      const blob = await objectivesAPI.downloadAttachment(objectiveId, attachmentId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error: any) {
      console.error('Error downloading file:', error);
      toast.error('Failed to download file');
    }
  };

  const handleFileDelete = async (objectiveId: number, attachmentId: number) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await objectivesAPI.deleteAttachment(objectiveId, attachmentId);
      toast.success('Attachment deleted successfully');
      loadObjectives();
      onObjectiveUpdated();
    } catch (error: any) {
      console.error('Error deleting attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'in_progress':
        return <Clock className="h-4 w-4 text-blue-600" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Project Objectives</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Add/Edit Form */}
          <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title *
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter objective title"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter description (optional)"
              />
            </div>
            <div className="flex justify-end space-x-3">
              {editingObjective && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? 'Saving...' : editingObjective ? 'Update' : 'Add Objective'}
              </button>
            </div>
          </form>

          {/* Objectives List */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Objectives</h3>
            {isLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading objectives...</p>
              </div>
            ) : objectives.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No objectives yet. Add one above to get started.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {objectives.map((objective) => (
                  <div key={objective.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {getStatusIcon(objective.status)}
                          <h4 className="font-medium text-gray-900">{objective.title}</h4>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(objective.status)}`}>
                            {objective.status.replace('_', ' ')}
                          </span>
                        </div>
                        {objective.description && (
                          <p className="text-sm text-gray-600 mb-2">{objective.description}</p>
                        )}
                        <div className="text-xs text-gray-500">
                          <span>Created by: {objective.created_by_name}</span>
                          <span className="ml-4">Created: {new Date(objective.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={() => handleEdit(objective)}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title="Edit objective"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(objective.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title="Delete objective"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* File Upload Section */}
                    <div className="border-t border-gray-200 pt-4">
                      <div className="flex items-center space-x-4 mb-3">
                        <input
                          type="file"
                          onChange={handleFileChange}
                          className="text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <button
                          onClick={() => handleFileUpload(objective.id)}
                          disabled={!selectedFile || uploadingFile === objective.id}
                          className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-1"
                        >
                          <Upload className="h-4 w-4" />
                          <span>{uploadingFile === objective.id ? 'Uploading...' : 'Upload'}</span>
                        </button>
                      </div>

                      {/* Attachments List */}
                      {objective.attachments && objective.attachments.length > 0 && (
                        <div>
                          <h5 className="text-sm font-medium text-gray-700 mb-2">Attachments:</h5>
                          <div className="space-y-2">
                            {objective.attachments.map((attachment) => (
                              <div key={attachment.id} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                                <div className="flex items-center space-x-2">
                                  <FileText className="h-4 w-4 text-gray-400" />
                                  <span className="text-sm text-gray-700">{attachment.original_filename}</span>
                                  <span className="text-xs text-gray-500">
                                    ({Math.round(attachment.file_size / 1024)} KB)
                                  </span>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleFileDownload(objective.id, attachment.id, attachment.original_filename)}
                                    className="text-blue-600 hover:text-blue-800 text-sm"
                                    title="Download"
                                  >
                                    <Download className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => handleFileDelete(objective.id, attachment.id)}
                                    className="text-red-600 hover:text-red-800 text-sm"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ObjectiveModal;
